export class Constants {
  public static readonly CHARACTER_LIMIT = {
    nameLength: 48,
    passwordMinimumLength: 6,
    passwordMaximumLength: 16,
    messagMaximumLength: 800,
    textareaMediumLength: 500,
    emailSubjectLength: 80,
    emailLength: 128,
    schoolNameLength: 120,
    domainLength: 120,
  };

  public static readonly PATTERN = {
    passwordPattern: /^(?=.*[A-Z])(?=.*[0-9])([a-zA-Z0-9@$!%*?&_()#])+$/,
    emailPattern: '^([a-zA-Z0-9_.+-])+@(([a-zA-Z0-9-])+.)+([a-zA-Z0-9]{2,4})+$',
    userName: '^[a-zA-Z ]*$',
    url: '(https?://)?([\\da-z.-]+)\\.([a-z.]{2,6})[/\\w .-]*/?',
    name: /^[a-zA-Z0-9\s]*$/,
    lastName: /^[a-zA-Z0-9\s\-]*$/,
    message: /^[a-zA-Z0-9\s?,.'@]*$/,
    messageIncludedWithSpanishLetters: /^[a-zA-Z0-9\s?,.'@áéíóúÁÉÍÓÚñÑ]*$/,
    emailOrNumber: /^(\d{10}|\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3}))$/,
    domainPattern: /^([\w-]+(?:\.[\w-]+)*)((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{1,6}(?:\.[a-z]{2})?)$/,
    licencePattern: /^[0-9]*$/,
    messageAdditionalChars: /^[a-zA-Z0-9\s?,.'@+\-=]*$/,
    messageAdditionalCharsWithSpanishLetters: /^[a-zA-Z0-9\s?,.'@+\-=áéíóúÁÉÍÓÚñÑ]*$/,
    positiveNumberPattern: /^[1-9][0-9]*$/,
    oneToTenNumberPattern: /^(10|[1-9])$/,
    // New patterns for buy-access-code component
    purchaserName: /^[a-zA-Z0-9\s\-]*$/,
    organization: /^[a-zA-Z0-9\s']*$/,
    emailWithPlus: /^[a-zA-Z0-9_.+\-@]*$/,
  };

  public static readonly ENDPOINTS = {
    login: '/v1/user/login',
    signUp: '/v1/user/register',
    socialLogin: '/v1/user/social-signin',
    socialSignup: '/v1/user/social-signup',
    logout: '/v1/user/logout',
    contactUs: '/v1/contact-us/query',
    forgotPassword: '/v1/password/forgot',
    changePassword: '/v1/password/change',
    resendVerificationLink: '/v1/user/resend-email-verification-mail',
    resendForgotLink: '/v1/password/resend-email-forgot-password',
    verifyEmail: '/v1/user/verify-email',
    resetPassword: '/v1/password/reset',
    addMathLevel: '/v1/math-levels/add',
    getMathLevel: '/v1/math-levels/get',
    selectMathLevel: '/v1/user/math-level',
    alterKeysSet: '/v1/alternate-keys/set',
    alterKeysGet: '/v1/alternate-keys/get',
    userPreferencesSet: '/v1/user-preferences/set',
    userPreferencesGet: '/v1/user-preferences/get',
    userToolsSet: '/v1/user-tools/set',
    userToolsGet: '/v1/user-tools/get',
    userGraphToolSet: '/v1/user-draw/set',
    userGraphToolGet: '/v1/user-draw/get',
    files: '/v1/files',
    startFile: '/v1/files/start-file',
    filesGet: '/v1/files/get',
    updateMathLevelId: '/v1/files/update-file-level-id',
    saveAsFile: '/v1/files/save-as',
    setTerms: '/v1/user/terms-condition',
    getPlans: '/v1/plans/get',
    subscription: '/v1/subscription',
    saveSubscription: '/v1/subscription/add',
    reportabug: '/v1/report-a-problem/submit',
    contactAdmin: '/v1/contact-us/query-after-login',
    applyLicenseCode: '/v1/user/apply-code-token',
    applyCodeEmail: '/v1/user/apply-code-email',
    setUserType: '/v1/user/user-type',
    getNewGoogleDriveFile: '/v1/files/get-new-google-drive-file',
    getGoogleDriveFileFromS3: '/v1/files/get-google-drive-file-s3-bucket',
    getGoogleDriveFileDetails: '/v1/files/get-google-drive-file-details',
    getMostRescentFileDetails: '/v1/files/get-user-last-opened-file',
    getClassroomDriveDetails: '/v1/files/drive-file-search',
    saveGoogleDrivePDFFile: '/v1/files/drive-pdf-file-save',
    saveGoogleDriveImageFile: '/v1/files/drive-image-file-save',
    updateGoogleIntegrationStatus: '/v1/user/update-user-integration-status',
    getGoogleDriveFileIdDetails: '/v1/files/get-google-drive-file-level-id',
    getAppVersion: '/v1/user/get-app-version',
    acknowledgeUpdate: '/v1/user/acknowledge-update',
    createAssignment: '/v1/assignments',
    getStudentAssignments: '/v1/assignments/student',
    getTeacherAssignments: '/v1/assignments/teacher',
    groupByAssignments: '/v1/assignments/groupby',
    getNewNotification: '/v1/assignments/new-notification',
    getFilesOfAssignment(assignmentId: string) {
      return `/v1/assignments/${assignmentId}/files`;
    },
    deleteAssignmentFolder(assignmentId: string) {
      return `/v1/assignments/${assignmentId}`;
    },
    deleteStudentAssignmentFolder(studentId: string) {
      return `/v1/assignments/student/${studentId}`;
    },
    clearAssignmentFolders: '/v1/assignments/folders/clear',
    submitAssignment: '/v1/assignments/submit',
    acceptInvite: '/v1/user/accept-invite',
    accountAccessCodeHasSubscriptionCancel: '/v1/subscription/account-access-code-has-subscription-cancel',
    checkForSubscriptionUpdate: '/v1/subscription/get-updated-subscription-planinfo',
    subscriptionResume: '/v1/subscription/resume',
  };

  public static readonly ADMIN_ENDPOINTS = {
    login: '/v1/admin/login',
    userList: '/v1/admin/users',
    domainRequest: '/v1/admin/domain-request',
    user: '/v1/admin/user',
    logout: '/v1/admin/logout',
    freeTrialUserList: '/v1/admin/free-trial-users',
    extendSubscription: '/v1/admin/subscription-extend',
    school: '/v1/admin/school',
    schoolList: '/v1/admin/school/users',
    editSubscription: '/v1/admin/school/license',
    createSiteAdmin: '/v1/admin/site-admin-creation',
    singleAccessCode: '/v1/admin/single-access-code',
    manageFreeSubscription: '/v1/admin/manage-free-subscriptions',
    manageResearcherAccount: '/v1/admin/manage-researcher-account',
  };

  public static readonly SCHOOL_ENDPOINTS = {
    signUp: '/v1/school/register',
    socialLogin: '/v1/school/social-signin',
    socialSignup: '/v1/school/social-signup',
    changePassword: '/v1/school/password/change',
    editProfile: '/v1/school/user/profile',
    manageAnalyticsVisibility: '/v1/school/user/manage-analytics',
    schoolStudents: '/v1/school/students',
    getStudentAnalytics: '/v1/school/user/get-analytics',
    unassignStudent: '/v1/school/student/unassign',
    logout: '/v1/school/logout',
    login: '/v1/school/login',
  };

  public static readonly TEACHER_ENDPOINTS = {
    student: '/v1/teacher/students',
    domainRequest: '/v1/teacher/domain-request',
    checkConnection: '/v1/teacher/connection-check',
    connectedTeachers: '/v1/teacher/connected-teachers',
  };

  public static readonly PUBLIC_DOMAINS = [
    'gmail.com',
    'yahoo.com',
    'outlook.com',
    'hotmail.com',
    'zoho.com',
    'comcast.net',
    'msn.com',
    'icloud.com',
    'cloud.microsoft',
  ];

  public static readonly messagesTimeout = 3000;

  public static readonly cssClasses = {
    sucess: 'success-msg',
    error: 'error-msg',
  };

  public static readonly ROLE_IDS = {
    USER: 1,
    ADMIN: 2,
    SCHOOL: 3,
    SITEADMIN: 4,
  };

  public static readonly STATIC_PAGES = {
    TERMS: 'Terms',
    PRIVACY: 'Privacy',
  };

  public static readonly USER_STATUS = {
    ACTIVE: 1,
    INACTIVE: 0,
  };

  public static readonly DOMAIN_REQUEST_STATUS = {
    PENDING: 'pending',
    ACCEPTED: 'accepted',
    REJECTED: 'rejected',
  };

  public static readonly ERROR_CODES = {
    BAD_REQUEST: 422,
    CONFLICT: 409,
    FORBIDDEN: 403,
    UNATHOURIZED: 401,
    NO_CONTENT: 204,
    SUCCESS: 200,
    TRANSACTION: 560,
    NOT_ACCEPTABLE: 406,
  };

  public static readonly SOCIAL_LOGIN_TYPES = {
    GOOGLE_SIGNUP: 'google_signup',
    GOOGLE_SIGNIN: 'google_sigin',
    MICROSOFT_SIGNUP: 'microsoft_signup',
    MICROSOFT_SIGNIN: 'microsoft_sigin',
    APPLE: 'apple',
    NATIVE: 'native',
    GOOGLE: 'google',
    MICROSOFT: 'microsoft',
    NYCDOE: 'nycdoe',
    CLEVER: 'clever',
  };

  public static readonly NUMERIC_VALUE = {
    zero: 0,
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
  };

  public static readonly LONG_PRESS_ALETR_KEYS_TIME = 1500;

  public static readonly PER_PAGE_LIMIT = 40;

  public static readonly ADMIN_PER_PAGE_LIMIT = 20;

  public static readonly FILE_STATUS = {
    NOT_DONE: '1',
    DONE: '2',
    TRASH: '3',
  };

  public static readonly EXPORT_FILE_STATUS = {
    DOWNLOAD_AS_PNG: '1',
    DOWNLOAD_AS_PDF: '2',
    DOWNLOAD_AS_KIWI: '3',
    EMAIL_A_PDF: '4',
  };

  public static readonly FILE_SAVE_TYPE = {
    EXPORT: 'export',
    PRINT: 'print',
  };

  public static readonly PLAN_TENURE_TYPE = {
    day: 1,
    month: 2,
    year: 3,
  };

  public static readonly ORDER_TYPE = {
    ASC: 1,
    DESC: -1,
    NA: 2,
  };

  public static readonly FILE_SAVE_INTERVAL = 60000; // 5 min 5 * 60 * 1000

  public static readonly FORCE_UPDATE_LOGOUT_TIMEOUT_MS = 4000;

  public static readonly SESSION_TIME = {
    Set_IdleTime: 3600, // 1 hour 60 * 60,
    Set_Timeout: 30, // 30 sec
  };

  public static readonly MIMES_LIST = [
    //List of mimes
    {
      mime: 'image/jpeg',
      pattern: [0xff, 0xd8, 0xff],
      mask: [0xff, 0xff, 0xff],
    },
    {
      mime: 'image/png',
      pattern: [0x89, 0x50, 0x4e, 0x47],
      mask: [0xff, 0xff, 0xff, 0xff],
    },
    {
      mime: 'application/pdf',
      pattern: [0x25, 0x50, 0x44, 0x46],
      mask: [0xff, 0xff, 0xff, 0xff],
    },
    {
      mime: 'application/kiwi',
      pattern: [0x7b],
      mask: [0xff],
    },
  ];

  public static readonly PREFERENCES_TAB = {
    GENERAL: 'GENERAL',
    MATH_LAYOUT: 'MATH_LAYOUT',
    COLOR: 'COLOR',
    ACCESSIBILITY: 'ACCESSIBILITY',
  };

  public static readonly GROUP_BY_ASSIGNMENT_OPTIONS = {
    GROUP_ASSIGNMENTS_BY_ASSIGNMENT: 'GROUP_ASSIGNMENTS_BY_ASSIGNMENT',
    GROUP_ASSIGNMENTS_BY_STUDENT: 'GROUP_ASSIGNMENTS_BY_STUDENT',
  };

  public static readonly IOS_SUPPORT = {
    MIN_SUPPORTED_VERSION: 16,
    MIN_SUPPORTED_VERSION_STRING: '16.0',
    UNSUPPORTED_MESSAGE: 'Your iOS version is not supported. Please update to iOS 16 or later to continue using this app.',
  };

  public static readonly GOOGLE_SUBSCRIPTION_LIST = ['dev_weekly'];

  /**
   * Google Play Billing configuration
   */
  public static readonly GOOGLE_PLAY = {
    PACKAGE_NAME: 'com.kiwiwrite.myapp.twa',
    BILLING_URL: 'https://play.google.com/billing',
    DEFAULT_CURRENCY: 'USD',
    SUBSCRIPTION_MANAGEMENT_URL: 'https://play.google.com/store/account/subscriptions',
    SUBSCRIPTION_TYPES: {
      STRIPE: 'stripe',
      GOOGLE: 'google',
      FREE: 'free',
    },
  };

  public static readonly SESSION_KEYS = {
    PENDING_GOOGLE_INTEGRATION_DISABLE: 'pendingGoogleIntegrationDisable',
    /** Last group-by the teacher chose on the assignments tab. */
    ASSIGNMENT_GROUP_BY: 'assignmentGroupBy',
  };

  public static readonly GOOGLE_ACCESS_TOKEN_WAIT_MS = 15_000;
}
